import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { ListLoader, ListLoaderDelegate, delegateActions } from '../components/list_loader';
import { Group } from '../models/group';
import * as groupsApi from '../api/groups';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { canEditGroup, reactListTablesEnabled } from '../permissions';
import { session } from '../session';
import { updateLocationWithQueryString } from '../utils';
import { GridView } from '../utils/grid_view';
import { GroupsListPage, GroupsListProps, GroupsListQuery, GroupsListRow } from 'react-islands';

let template = require('raw-loader!../../templates/groups.html').default;

class GroupsScreen implements ListLoaderDelegate<groupsApi.BaseGroupData, Group> {
  loader: ListLoader<groupsApi.BaseGroupData, Group>;
  canEdit = canEditGroup();
  listActions = ko.observableArray<ListHeaderAction>([]);

  searchPlaceholder = i18n.t('Search groups by name')();
  nameFilter = ko.observable('').extend({ rateLimit: { timeout: 400, method: 'notifyWhenChangesStop' } });

  newGroupsTable = reactListTablesEnabled();

  private subscriptions: KnockoutSubscription[] = [];

  private view = new GridView('groups');

  tableProps = ko.pureComputed<GroupsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
  }));

  constructor(params: { filters: { search: string } }) {
    if (this.canEdit) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/groups/new/',
        tooltipTitle: i18n.t('Add group')(),
      });
    }

    if (this.newGroupsTable) {
      this.view.load(params.filters);
    } else if (params.filters.search) {
      this.nameFilter(params.filters.search);
    }
  }

  onReady(loader: ListLoader<groupsApi.GroupData, Group>): void {
    this.loader = loader;
    this.subscriptions.push(this.nameFilter.subscribe(() => this.loader.forceLoad()));
  }

  fetch(params: ListRequestParams) {
    const filters = {
      search: this.nameFilter(),
    };
    updateLocationWithQueryString(filters);

    return groupsApi.list({
      ...filters,
      ...params,
    });
  }

  private fetchPage = (query: GroupsListQuery): Promise<GroupsListPage> =>
    // The endpoint ANDs `search` with the filter.
    this.view.fetchListPage(query, groupsApi.list, 'search', this);

  toRow = (group: Group): GroupsListRow => ({
    id: group.id(),
    name: group.name(),
    url: session.toTenantPath(group.editUrl()),
    actions: delegateActions(this, group, this.view.reload),
  });

  instantiate(groupData: groupsApi.BaseGroupData) {
    return new Group(false, groupData);
  }

  remove(id: string) {
    return groupsApi.remove(id);
  }

  canRemove(group: Group) {
    return this.canEdit && !group.isGlobal();
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let groups = {
  name: 'groups',
  viewModel: GroupsScreen,
  template: template,
};

ko.components.register(groups.name, groups);
